import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { FieldSelection, GlobalFieldId } from '#layers/data-model';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { CollectionFieldPath } from '#layers/data-model/utils/collection-field-path';
import { computed, toValue } from 'vue';
import { toCollectionFieldPathKey } from '#layers/data-model/utils/collection-field-path';
import { resolveFieldPathSelection } from '#layers/data-model/utils/display-template-field-selection';
import { useFieldSelectionContexts } from '~/composables/use-field-selection-contexts';
import { idFieldSelectionToFieldSelection } from '~/utils/id-field-selection-to-field-selection';
import { mergeFieldSelections } from '~/utils/merge-field-selection';

export function useTableQueryFields(
	collection: MaybeRefOrGetter<ResolvedMonospaceCollection | undefined>,
	visibleColumnPaths: MaybeRefOrGetter<CollectionFieldPath[]>,
): ComputedRef<FieldSelection> {
	const { fieldSelectionCtx, idCtx } = useFieldSelectionContexts();

	// Cache each path's field selection per collection so column edits only resolve the delta.
	const cache = new WeakMap<ResolvedMonospaceCollection, Map<string, FieldSelection>>();

	return computed<FieldSelection>(() => {
		const col = toValue(collection);
		const paths = toValue(visibleColumnPaths);

		if (!col || paths.length === 0) return ['*'];

		let perCollection = cache.get(col);

		if (!perCollection) {
			perCollection = new Map<string, FieldSelection>();
			cache.set(col, perCollection);
		}

		const perPathSelections: FieldSelection[] = [];

		for (const fieldPath of paths) {
			const pathKey = toCollectionFieldPathKey(fieldPath);
			let fieldSelection = perCollection.get(pathKey);

			if (!fieldSelection) {
				const idSelection = resolveFieldPathSelection(col, fieldPath as unknown as GlobalFieldId[], fieldSelectionCtx);
				fieldSelection = idFieldSelectionToFieldSelection(idSelection, idCtx);

				perCollection.set(pathKey, fieldSelection);
			}

			perPathSelections.push(fieldSelection);
		}

		return mergeFieldSelections(...perPathSelections);
	});
}
